// Labels and error copy for the vendor screens. Keys mirror the server's
// vendorFields.js; the wording is spec 4.2/5.4 verbatim.

export const LEVELS = ['view', 'comment', 'propose', 'approve'];
export const levelRank = (level) => LEVELS.indexOf(level);
export const atLeast = (level, min) => levelRank(level) >= 0 && levelRank(level) >= levelRank(min);

// Does /api/vendors/me say the caller holds at least `min` on any speciality?
export const holdsLevel = (me, min) => !!me && (!!me.is_admin || (me.levels || []).some((l) => atLeast(l.level, min)));

// Highest level the caller holds over a vendor's speciality ids. Admins are
// approve everywhere; see-all alone is view. Null when none.
export function levelForTags(me, tagIds) {
  if (!me) return null;
  if (me.is_admin) return 'approve';
  const ids = new Set(tagIds || []);
  let best = me.see_all ? 'view' : null;
  (me.levels || []).forEach((l) => {
    if (ids.has(l.tag_id) && levelRank(l.level) > levelRank(best)) best = l.level;
  });
  return best;
}

export const LEVEL_LABEL = {
  view: 'View',
  comment: 'Comment',
  propose: 'Propose',
  approve: 'Approve',
};

export const RISK_LABEL = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };
export const DATA_ACCESS_LABEL = {
  none: 'None',
  internal: 'Internal',
  confidential: 'Confidential',
  employees: 'Employee data',
  customers: 'Customer data',
};
export const NDA_LABEL = { not_required: 'Not required', pending: 'Pending', signed: 'Signed', expired: 'Expired' };
export const CONTRACT_LABEL = { none: 'None', draft: 'Draft', active: 'Active', expired: 'Expired', terminated: 'Terminated' };

export const REVIEW_STATUS_LABEL = {
  overdue: 'Overdue',
  due_soon: 'Due in 30 days',
  ok: 'OK',
  never_reviewed_due: 'Never reviewed',
};

// Values the list's review filter sends (the server's names, which differ from
// the per-row review_status keys for "never reviewed").
export const REVIEW_FILTER_OPTIONS = [
  ['overdue', 'Overdue'],
  ['due_soon', 'Due in 30 days'],
  ['ok', 'OK'],
  ['never_reviewed', 'Never reviewed'],
];

// Odoo sends UTC date-times as 'YYYY-MM-DD HH:MM:SS' with no zone; read as UTC.
export function odooDateTime(v) {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2} \d/.test(v) ? `${v.replace(' ', 'T')}Z` : v;
}

export const REVIEW_OUTCOME_LABEL = {
  satisfactory: 'Satisfactory',
  issues_found: 'Issues found',
  terminate_recommended: 'Recommend ending the relationship',
};

export const DOC_TYPE_LABEL = { contract: 'Contract', nda: 'NDA', certificate: 'Certificate', other: 'Other' };

export const PROPOSAL_STATUS_LABEL = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  failed: 'Failed',
  stale: 'Out of date',
};

export const PROPOSAL_KIND_LABEL = {
  create: 'New vendor',
  edit: 'Edit',
  retag: 'Change specialities',
  archive: 'Archive',
};

// Field labels for proposal diffs and the detail view (server VENDOR_FIELDS).
export const FIELD_LABEL = {
  name: 'Name',
  is_company: 'Is a company',
  street: 'Street',
  street2: 'Street 2',
  city: 'City',
  zip: 'ZIP',
  country_id: 'Country',
  state_id: 'State',
  website: 'Website',
  email: 'Email',
  phone: 'Phone',
  vat: 'Tax ID',
  company_registry: 'Company registry',
  risk_level: 'Risk level',
  data_access_scope: 'Data access scope',
  nda_status: 'NDA status',
  nda_date: 'NDA date',
  contract_status: 'Contract status',
  contract_end: 'Contract end',
};

// Display text for one field value (selection keys become their label).
const VALUE_LABELS = {
  risk_level: RISK_LABEL,
  data_access_scope: DATA_ACCESS_LABEL,
  nda_status: NDA_LABEL,
  contract_status: CONTRACT_LABEL,
};
export function fieldValueLabel(field, value) {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (typeof value === 'boolean') return value ? 'Company' : 'Individual';
  const map = VALUE_LABELS[field];
  if (map && map[value]) return map[value];
  if (typeof value === 'object') return value.name || value.label || String(value.id ?? '');
  return String(value);
}

export const NDA_WARNING = "High and critical vendors need a signed NDA. You can submit, but the vendor will be flagged until it's signed.";
export const ISO_MISSING = "ISO fields aren't set up in Odoo yet.";

// Server `{error:'code'}` to human text, for errorMessage()'s third argument.
const ODOO_DOWN = "Couldn't reach Odoo. Nothing was saved — try again in a minute.";
export const VENDOR_ERRORS = {
  not_found: "This vendor doesn't exist or isn't in your specialities.",
  pending_exists: "There's already a pending change for this vendor.",
  stale_proposal: 'This vendor changed in Odoo since the proposal was made. Ask for a new proposal.',
  vendor_archived: 'This vendor was archived in Odoo.',
  self_approval: "You can't approve your own request.",
  iso_fields_missing: ISO_MISSING,
  odoo_unavailable: ODOO_DOWN,
  odoo_access_denied: ODOO_DOWN,
  bad_file_type: 'Upload a PDF, JPG or PNG.',
  file_too_large: 'Files must be 5 MB or smaller.',
  risk_required: 'Set a risk level before recording the review.',
  already_tagged: 'Someone already tagged this vendor.',
  already_has_access: 'You already have this level or higher.',
  nothing_to_acknowledge: 'Nothing to acknowledge. It was already cleared.',
  no_changes: 'Nothing changed.',
  not_pending: 'This request was already handled.',
  rate_limited: 'Too many requests. Wait a minute and try again.',
  odoo_unconfirmed: "Odoo didn't confirm — check Odoo before proposing again.",
  requester_inactive: "This person's account is deactivated.",
  not_speciality: 'That tag is no longer a speciality.',
  self_grant_forbidden: "You can't grant access to yourself.",
  user_inactive: "That person's account is deactivated.",
  sync_running: 'A sync is already running.',
  forbidden: "You don't have permission to do that.",
  risk_change_needs_proposal: 'Risk level already set — propose a change instead.',
};

// A failed proposal's `failure_reason` as a line under its Failed status. Unknown reasons get a
// generic line rather than nothing: the person must learn the change did not reach Odoo.
const FAILURE_COPY = {
  odoo_unconfirmed: VENDOR_ERRORS.odoo_unconfirmed,
  iso_fields_missing: ISO_MISSING,
  unknown_field: ISO_MISSING,
  odoo_access_denied: ODOO_DOWN,
  odoo_unavailable: ODOO_DOWN,
};
export const failureMessage = (reason) => FAILURE_COPY[reason] || "The change couldn't be saved to Odoo.";
